import { Check } from "lucide-react";
import { useMemo } from "react";

import { useListAgendaItems } from "@elation/api-client/src/stitch-web";

import { AgendaItemEntity } from "@src/features/agenda";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Avatar, AvatarFallback } from "@src/shared/components/ui/avatar";

import type { PatientSelectorPatientSummaryV2 } from "../../PatientSelector";

export interface CompletedVisitHeaderProps {
  patient?: PatientSelectorPatientSummaryV2 | null;
  encounterId?: number;
}

export function CompletedVisitHeader({
  patient,
  encounterId,
}: CompletedVisitHeaderProps) {
  const agendaItemsQuery = useListAgendaItems(encounterId ?? 0, {
    query: {
      enabled: encounterId != null,
      refetchInterval: encounterId != null ? 5000 : false,
    },
  });
  const agendaItems = useMemo(
    () =>
      (agendaItemsQuery.data?.data.items ?? []).filter(
        AgendaItemEntity.isActionable,
      ),
    [agendaItemsQuery.data],
  );
  const addressedCount = agendaItems.filter(
    AgendaItemEntity.isCompleted,
  ).length;
  const agendaCount = agendaItems.length;
  const statusLabel =
    agendaCount > 0
      ? `${addressedCount}/${agendaCount} addressed`
      : "visit complete";

  return (
    <VerticalFlexbox className="shrink-0 border-border border-b bg-background">
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="space-between"
        className="gap-3 px-[18px] py-3"
      >
        <HorizontalFlexbox alignItems="center" className="min-w-0 gap-2.5">
          <Avatar className="size-8 border border-border bg-background">
            <AvatarFallback className="bg-primary/10 font-mono text-[10px] font-semibold text-primary">
              {patient?.initials ?? "PT"}
            </AvatarFallback>
          </Avatar>
          <VerticalFlexbox className="min-w-0 leading-tight">
            <p className="truncate text-[13.5px] font-semibold text-foreground">
              {patient?.name ?? "Completed visit"}
            </p>
            <p className="mt-0.5 truncate font-mono text-[10.5px] text-muted-foreground">
              {patient?.metadata ?? "Review note, codes, and transcript"}
            </p>
          </VerticalFlexbox>
        </HorizontalFlexbox>

        <HorizontalFlexbox
          alignItems="center"
          className="gap-1 text-[11px] font-medium text-success"
          role="status"
          aria-live="polite"
        >
          <Check className="size-3.5" aria-hidden="true" />
          {statusLabel}
        </HorizontalFlexbox>
      </HorizontalFlexbox>
    </VerticalFlexbox>
  );
}
